import type { TFunction } from 'i18next';

import type { MonitoringLinkLifecycle, MonitoringLinkListItem } from '../types/api';
import { humanizeKey } from './reportFields';

/**
 * Where the monitoring check app is hosted. The link sent to a customer is
 * `<base>/?token=<token>`. Left unset, the admin shows the token on its own.
 */
const LINK_BASE_URL = (import.meta.env.VITE_MONITORING_LINK_BASE_URL as string | undefined)
    ?.trim()
    .replace(/\/+$/, '');

export function buildMonitoringLinkUrl(token: string): string | null {
    return LINK_BASE_URL ? `${LINK_BASE_URL}/?token=${encodeURIComponent(token)}` : null;
}

export const LIFECYCLE_COLORS: Record<MonitoringLinkLifecycle, string> = {
    live: 'green',
    submitted: 'blue',
    expired: 'gray',
    revoked: 'red',
    superseded: 'gray',
};

export const LIFECYCLE_ORDER: MonitoringLinkLifecycle[] = [
    'live',
    'submitted',
    'expired',
    'revoked',
    'superseded',
];

/** Expiry choices in hours. The API defaults to 7 days and allows up to 60. */
export const EXPIRY_HOURS_OPTIONS = [24, 72, 168, 336, 720, 1440] as const;
export const DEFAULT_EXPIRY_HOURS = 168;

/** Readable label for a progress event name; unknown names are humanized. */
export function progressStageLabel(t: TFunction, stage: string | null | undefined): string {
    if (!stage) {
        return t('features.monitoringLinks.stages.notStarted');
    }
    return t(`features.monitoringLinks.stages.${stage}`, { defaultValue: humanizeKey(stage) });
}

export function formatDateTime(value: string | null | undefined): string | null {
    return value ? new Date(value).toLocaleString() : null;
}

/** When the link ends (or ended), worded for its lifecycle. */
export function linkEndLabel(t: TFunction, link: MonitoringLinkListItem): string {
    const keys: Record<MonitoringLinkLifecycle, [string, string | null]> = {
        live: ['expiresOn', link.expiresAt],
        expired: ['expiredOn', link.expiresAt],
        submitted: ['submittedOn', link.submittedAt],
        revoked: ['revokedOn', link.revokedAt],
        superseded: ['replacedOn', link.supersededAt],
    };
    const [key, at] = keys[link.lifecycle] ?? keys.live;
    return t(`features.monitoringLinks.${key}`, { when: formatDateTime(at) ?? '' });
}
